import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { storeTokens, clearTokens, hasStoredToken } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.search?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Always check auth with the server to get fresh role/user data
    const token = hasStoredToken();
    if (token) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    storeTokens(res.data);
    // Fetch fresh user data from server (role may have changed)
    const me = await api.get('/auth/me');
    setUser(me.data);
    return me.data;
  };

  const register = async (email, password, name) => {
    const res = await api.post('/auth/register', { email, password, name });
    storeTokens(res.data);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (err) { console.error('Logout failed:', err); }
    clearTokens();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
